import { CustomClaimValue, Devices, ForwardedPortBase } from "@app/shared";
import { IconLink, Icons, SortableTable, Spinner, StatusDot } from ".";
import { toastSuccess } from "../../helpers";
import { useConfirmDialog, useHasClaim } from "../../hooks";
import { format } from "date-fns";
import copy from "copy-to-clipboard";
import { ReactElement, ReactNode } from "react";
import { HoverTooltip } from "./HoverTooltip";

type DevicePortsTableProps = {
    ports: ForwardedPortBase[] | undefined;
    isLoading?: boolean;
    /** Called after a port was closed or a pending request cancelled from this table. */
    onPortsChanged?: (port: ForwardedPortBase) => void;
};

export const DevicePortsTable: React.FC<DevicePortsTableProps> = ({ ports, isLoading, onPortsChanged }) => {
    const confirmDialog = useConfirmDialog();
    const isSuperAdmin = useHasClaim(CustomClaimValue.SuperAdmin);

    const handleCloseClick = (fp: ForwardedPortBase) => {
        const isOpen = !!fp.deviceConnected;
        confirmDialog.show(
            isOpen ? "Close forwarded port?" : "Cancel port request?",
            isOpen
                ? `Close the tunnel on server port ${fp.serverPort}? Anyone connected through it will be disconnected, and the device will resume checking for new requests.`
                : "Cancel this pending port request so the device no longer picks it up?",
            isOpen ? "Close Port" : "Cancel Request",
            async () => {
                await Devices.closeForwardedPort(fp.id);
                toastSuccess(isOpen ? "Port Closed" : "Request Cancelled", isOpen ? "The forwarded port was closed." : "The pending request was cancelled.");
                onPortsChanged?.(fp);
            }
        );
    };
    const formatTime = (date?: Date) => {
        if (!date) { return undefined; }
        return format(date, 'MMM dd yyyy HH:mm a XXX');
    }

    const getUri = (fp?: ForwardedPortBase) => fp?.serverPort ? textWithCopyBtn((!!!fp?.serverPublicHostname) ? '' : `ssh omnisight@${fp.serverPublicHostname} -p ${fp?.serverPort}`) : '';
    const textWithCopyBtn = (txt?: string) => {
        if (txt?.length) {
            return (<><span>{txt}</span><IconLink className="ml-3" Icon={Icons.Clipboard} onClick={() => copy(txt)} /></>)
        }
    }
    // A request the device never picked up is "failed" once the server terminated it (expiry or a
    // cancel from this table), or once it has been waiting longer than the timeout.
    // Keep REQUEST_TIMEOUT_MS in sync with PortInfoProvider.RequestTimeout.
    const REQUEST_TIMEOUT_MS = 10 * 60 * 1000;
    const isFailed = (fp?: ForwardedPortBase) => {
        if (!fp || fp.deviceConnected) { return false; }
        if (fp.terminated) { return true; }
        if (!fp.requested) { return false; }
        return Date.now() - new Date(fp.requested).getTime() > REQUEST_TIMEOUT_MS;
    };
    const portStatus = (fp?: ForwardedPortBase) => {
        if (isFailed(fp)) {
            return hoverWrap((<StatusDot status={'error'} />), 'Failed — the device never picked up this request, or it was cancelled');
        }
        if (!!!fp?.deviceConnected) {
            return hoverWrap((<div className="h-2 w-[18px] mx-auto"><Spinner /></div>), 'Waiting for device');
        }
        const isOpen = !!!fp?.terminated;
        return hoverWrap((<StatusDot status={isOpen ? 'ok' : undefined} />), isOpen ? 'Port Open' : 'Port Closed');
    };
    const hoverWrap = (ele: ReactNode | ReactElement, txt: string) => {
        return (<HoverTooltip hoverable={ele}>{txt}</HoverTooltip>);
    };

    return (
        <>
            <SortableTable
                isLoading={isLoading}
                initialSortDir="desc"
                initialSortKey="requested"
                emptyValue={<div className="py-10">No forwarded ports were found</div>}
                columns={[
                    {
                        header: "",
                        renderFunc: portStatus,
                        width: 25
                    },
                    {
                        header: "Requested",
                        dataKey: "requested",
                        renderFunc: dc => formatTime(dc?.requested),
                        width: 200
                    },
                    {
                        header: "Requested By",
                        dataKey: "requestedByName",
                        renderFunc: fp => fp?.requestedByName || '—',
                        width: 180
                    },
                    {
                        header: "Device Port",
                        width: 200,
                        dataKey: "clientPort"
                    },
                    {
                        header: "Server Port",
                        width: 200,
                        dataKey: "serverPort",
                        renderFunc: fp => fp?.serverPort ? textWithCopyBtn(fp?.serverPort?.toString()) : '',
                    },
                    {
                        header: "Uri",
                        width: 200,
                        dataKey: "serverPort",
                        renderFunc: fp => getUri(fp),
                    },
                    {
                        header: "Connection Started",
                        width: 200,
                        dataKey: "deviceConnected",
                        renderFunc: dc => formatTime(dc?.deviceConnected),
                    },
                    {
                        header: "Connection Terminated",
                        width: 200,
                        dataKey: "terminated",
                        renderFunc: dc => formatTime(dc?.terminated),
                    },
                    // Super-admins get a close/cancel action on every row that is still open or pending.
                    ...(isSuperAdmin ? [{
                        header: "",
                        width: 40,
                        renderFunc: (fp?: ForwardedPortBase) => fp && !fp.terminated ? hoverWrap(
                            <IconLink Icon={Icons.Close} onClick={() => handleCloseClick(fp)} />,
                            fp.deviceConnected ? 'Close this port' : 'Cancel this request'
                        ) : null,
                    }] : []),
                ]}
                data={ports}
                pagination
                defaultPageSize={15}
                pageSizeOptions={[10, 15, 25, 50, 100]}
            />
            {confirmDialog.renderDialog()}
        </>
    );
}
